/**
 * Configuration des travaux du calculateur d'aides — découpée par catégorie.
 *
 * Chaque travail expose :
 *  - Sa catégorie (`category`) : utilisée pour le groupement visuel à l'étape 4.
 *  - Ses tags d'éligibilité (`tags`) : MPR / CEE / Coup-de-pouce / Éco-PTZ / TVA 5,5.
 *  - Ses fourchettes de coûts et de primes CEE (`costMin/Max`, `ceeMin/Max`) utilisées
 *    UNIQUEMENT par le moteur de calcul (`compute.ts`) — invisibles dans l'UI étape 4.
 *
 * Les montants finaux n'apparaissent qu'à l'étape Résultat.
 */

import {
  Layers,
  Flame,
  Wind,
  ClipboardCheck,
  Sparkles,
  type LucideIcon,
} from 'lucide-react';

/* ───────────────────────────────────────────────────────────
   Tags d'éligibilité
─────────────────────────────────────────────────────────── */

export type TravauxTag = 'mpr' | 'cee' | 'coup-pouce' | 'eco-ptz' | 'tva-55';

export const TRAVAUX_TAG_META: Record<
  TravauxTag,
  { label: string; full: string; tone: 'brand' | 'accent' | 'lavender' | 'sage' | 'amber' }
> = {
  mpr: {
    label: "MPR",
    full: "MaPrimeRénov’",
    tone: 'brand',
  },
  cee: {
    label: 'CEE',
    full: "Primes Certificats d'Économie d'Énergie",
    tone: 'accent',
  },
  'coup-pouce': {
    label: 'Coup de pouce',
    full: 'CEE bonifié — Coup de pouce',
    tone: 'amber',
  },
  'eco-ptz': {
    label: 'Éco-PTZ',
    full: "Éco-prêt à taux zéro (jusqu'à 50 000 €)",
    tone: 'lavender',
  },
  'tva-55': {
    label: 'TVA 5,5 %',
    full: 'TVA réduite à 5,5 % au lieu de 20 %',
    tone: 'sage',
  },
};

export const TAG_TONE_CLASSES: Record<
  'brand' | 'accent' | 'lavender' | 'sage' | 'amber',
  string
> = {
  brand: 'bg-brand-100 text-brand-800 ring-brand-200/60',
  accent: 'bg-accent-100 text-accent-900 ring-accent-200/60',
  lavender: 'bg-lavender-100 text-lavender-800 ring-lavender-200/60',
  sage: 'bg-sage-100 text-sage-700 ring-sage-200/60',
  amber: 'bg-amber-100 text-amber-800 ring-amber-200/60',
};

/* ───────────────────────────────────────────────────────────
   Catégories de travaux
─────────────────────────────────────────────────────────── */

export type TravauxCategory =
  | 'globale'
  | 'isolation'
  | 'chauffage-ecs'
  | 'ventilation'
  | 'audit';

export interface TravauxCategoryMeta {
  id: TravauxCategory;
  label: string;
  sublabel: string;
  description: string;
  icon: LucideIcon;
  /** "exclusive" = un seul item de cette catégorie peut être combiné avec d'autres parcours. */
  variant: 'global' | 'gestes';
}

export const TRAVAUX_CATEGORIES: TravauxCategoryMeta[] = [
  {
    id: 'globale',
    label: 'Rénovation globale',
    sublabel: "Parcours d'ampleur — bonus de coordination",
    description:
      "Un bouquet de travaux coordonné qui ouvre droit à MaPrimeRénov’ Parcours accompagné (ex-Rénovation d'ampleur) — plafond jusqu'à 63 000 €.",
    icon: Sparkles,
    variant: 'global',
  },
  {
    id: 'isolation',
    label: 'Isolation',
    sublabel: 'Combles, murs, sol, fenêtres',
    description:
      "Réduisez les déperditions thermiques. Cumulable MPR + CEE pour les ménages éligibles.",
    icon: Layers,
    variant: 'gestes',
  },
  {
    id: 'chauffage-ecs',
    label: 'Chauffage & ECS',
    sublabel: "Pompe à chaleur, biomasse, eau chaude solaire",
    description:
      "Remplacer un système fossile par un équipement renouvelable. Bonus Coup de pouce CEE en vigueur.",
    icon: Flame,
    variant: 'gestes',
  },
  {
    id: 'ventilation',
    label: 'Ventilation',
    sublabel: 'VMC double flux à échangeur',
    description:
      "Préserver la qualité de l'air et récupérer la chaleur sortante après isolation.",
    icon: Wind,
    variant: 'gestes',
  },
  {
    id: 'audit',
    label: 'Audit énergétique',
    sublabel: "Pré-requis du parcours d'ampleur",
    description:
      "Diagnostic obligatoire pour bénéficier de MPR Parcours accompagné (Rénovation d'ampleur).",
    icon: ClipboardCheck,
    variant: 'gestes',
  },
];

/* ───────────────────────────────────────────────────────────
   Travaux disponibles
─────────────────────────────────────────────────────────── */

export interface TravauxOption {
  id: string;
  category: TravauxCategory;
  label: string;
  sublabel: string;
  /** Tags d'éligibilité (badges visibles dans l'UI étape 4). */
  tags: TravauxTag[];
  /** Fourchette de coût moyen TTC du chantier (€). */
  costMin: number;
  costMax: number;
  /** Fourchette indicative de prime CEE (€). */
  ceeMin: number;
  ceeMax: number;
  /** Éligible MaPrimeRénov’ (passé au moteur de calcul). */
  eligibleMPR: boolean;
}

export const TRAVAUX_OPTIONS: TravauxOption[] = [
  /* ── Rénovation globale ──────────────────────────────── */
  {
    id: 'reno-globale',
    category: 'globale',
    label: "Parcours d'ampleur (3+ gestes)",
    sublabel: 'Avec accompagnement Mon Accompagnateur Rénov',
    tags: ['mpr', 'cee', 'eco-ptz', 'tva-55'],
    costMin: 25_000,
    costMax: 80_000,
    ceeMin: 3_000,
    ceeMax: 12_000,
    eligibleMPR: true,
  },

  /* ── Isolation ───────────────────────────────────────── */
  {
    id: 'combles',
    category: 'isolation',
    label: 'Isolation des combles / toiture',
    sublabel: 'Combles perdus ou aménagés, toiture-terrasse',
    tags: ['mpr', 'cee', 'coup-pouce', 'eco-ptz', 'tva-55'],
    costMin: 3_000,
    costMax: 15_000,
    ceeMin: 600,
    ceeMax: 3_000,
    eligibleMPR: true,
  },
  {
    id: 'murs',
    category: 'isolation',
    label: 'Isolation des murs extérieurs',
    sublabel: 'ITE, bardage, enduit isolant',
    tags: ['mpr', 'cee', 'eco-ptz', 'tva-55'],
    costMin: 8_000,
    costMax: 25_000,
    ceeMin: 800,
    ceeMax: 4_000,
    eligibleMPR: true,
  },
  {
    id: 'sol',
    category: 'isolation',
    label: 'Isolation du plancher bas',
    sublabel: 'Vide sanitaire, sous-sol non chauffé',
    tags: ['mpr', 'cee', 'eco-ptz', 'tva-55'],
    costMin: 2_000,
    costMax: 8_000,
    ceeMin: 400,
    ceeMax: 1_500,
    eligibleMPR: true,
  },
  {
    id: 'fenetres',
    category: 'isolation',
    label: 'Fenêtres & menuiseries',
    sublabel: 'Double ou triple vitrage isolant',
    tags: ['mpr', 'cee', 'eco-ptz', 'tva-55'],
    costMin: 3_000,
    costMax: 12_000,
    ceeMin: 300,
    ceeMax: 1_200,
    eligibleMPR: true,
  },

  /* ── Chauffage & ECS ─────────────────────────────────── */
  {
    id: 'pac',
    category: 'chauffage-ecs',
    label: 'Pompe à chaleur',
    sublabel: 'Air/eau, géothermique, hybride',
    tags: ['mpr', 'cee', 'coup-pouce', 'eco-ptz', 'tva-55'],
    costMin: 8_000,
    costMax: 18_000,
    ceeMin: 1_500,
    ceeMax: 5_000,
    eligibleMPR: true,
  },
  {
    id: 'biomasse',
    category: 'chauffage-ecs',
    label: 'Chaudière biomasse / bois',
    sublabel: 'Granulés, bûches, déchiqueté',
    tags: ['mpr', 'cee', 'coup-pouce', 'eco-ptz', 'tva-55'],
    costMin: 5_000,
    costMax: 20_000,
    ceeMin: 1_000,
    ceeMax: 3_500,
    eligibleMPR: true,
  },
  {
    id: 'solaire-thermique',
    category: 'chauffage-ecs',
    label: 'Chauffe-eau solaire / système combiné',
    sublabel: 'SSC, CESI, capteurs thermiques',
    tags: ['mpr', 'cee', 'eco-ptz', 'tva-55'],
    costMin: 4_000,
    costMax: 12_000,
    ceeMin: 500,
    ceeMax: 2_500,
    eligibleMPR: true,
  },
  {
    id: 'ceth',
    category: 'chauffage-ecs',
    label: 'Chauffe-eau thermodynamique',
    sublabel: 'PAC dédiée ECS — alternative au cumulus',
    tags: ['mpr', 'cee', 'eco-ptz', 'tva-55'],
    costMin: 2_500,
    costMax: 5_500,
    ceeMin: 300,
    ceeMax: 1_200,
    eligibleMPR: true,
  },

  /* ── Ventilation ─────────────────────────────────────── */
  {
    id: 'vmc',
    category: 'ventilation',
    label: 'VMC double flux à haut rendement',
    sublabel: 'Avec échangeur thermique (≥ 85 %)',
    tags: ['mpr', 'cee', 'eco-ptz', 'tva-55'],
    costMin: 1_500,
    costMax: 4_000,
    ceeMin: 300,
    ceeMax: 800,
    eligibleMPR: true,
  },

  /* ── Audit ───────────────────────────────────────────── */
  {
    id: 'audit',
    category: 'audit',
    label: 'Audit énergétique réglementaire',
    sublabel: 'Forfait MPR — requis pour le parcours accompagné',
    tags: ['mpr', 'tva-55'],
    costMin: 500,
    costMax: 1_200,
    ceeMin: 0,
    ceeMax: 0,
    eligibleMPR: false,
  },
];

export function getTravauxByCategory(category: TravauxCategory): TravauxOption[] {
  return TRAVAUX_OPTIONS.filter((t) => t.category === category);
}

export function findTravaux(id: string): TravauxOption | undefined {
  return TRAVAUX_OPTIONS.find((t) => t.id === id);
}
